import { SlidersHorizontal } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  CHAIN_ADDON_HINT,
  CHAIN_ADDON_LABEL,
  type ChainAddon,
} from './chainAddons'

/**
 * Putting a shed column back on a split chain — ROADMAP 9.25.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * IT COSTS NO ROW, AND THAT IS A REQUIREMENT RATHER THAN A FLOURISH
 *
 * A control for making the ladder taller must not itself be a band across
 * the screen. It lives in the LEGEND — the strip under the table that
 * already explains the ITM tint and the OI bar — so the whole feature costs
 * the chain nothing: the legend's row is drawn whether this is in it or not.
 *
 * A toolbar above the chain was the obvious alternative and it is
 * self-defeating: ~34px of chrome to recover ~110px of columns, on exactly
 * the layout where vertical space is scarcest because the drawer has taken
 * the width.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ONLY WHEN IT CONTROLS SOMETHING
 *
 * The caller mounts this only on a SPLIT chain, and passes only the add-ons
 * the current mode actually draws. At full width nothing is shed, so every
 * box would be ticked and disabled — a control whose every state is the same
 * state. Greeks has no ITM% column to offer and Combined offers neither.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function ChainAddons({
  offered,
  value,
  onChange,
}: {
  /** The add-ons this mode draws. Empty means the caller should not mount this. */
  offered: readonly ChainAddon[]
  /** Which of them are currently asked back. */
  value: readonly ChainAddon[]
  onChange: (next: ChainAddon[]) => void
}) {
  if (offered.length === 0) return null
  const toggle = (addon: ChainAddon, on: boolean): void =>
    onChange(on ? [...value.filter((a) => a !== addon), addon] : value.filter((a) => a !== addon))

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" className="tr-addons" data-testid="chain-addons">
          <SlidersHorizontal size={12} />
          Columns
          {value.length > 0 && <span className="tr-addons-n">{value.length}</span>}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" side="top" className="w-60">
        <DropdownMenuLabel className="text-[11px] font-normal text-muted-foreground">
          Hidden while the drawer is open
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {offered.map((addon) => (
          <DropdownMenuCheckboxItem
            key={addon}
            checked={value.includes(addon)}
            /*
             * Radix closes the menu on select by default. Ticking two
             * columns back on is two round trips through a control that has
             * to be found again each time, so the menu stays open and the
             * chain updates behind it — which is also the only way to see
             * what the choice did.
             */
            onSelect={(e) => e.preventDefault()}
            onCheckedChange={(on) => toggle(addon, on === true)}
            data-testid={`chain-addon-${addon}`}
          >
            <span className="flex flex-col gap-0.5">
              <span>{CHAIN_ADDON_LABEL[addon]}</span>
              <span className="text-[10.5px] text-muted-foreground">
                {CHAIN_ADDON_HINT[addon]}
              </span>
            </span>
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
